import { useMemo } from 'react'
import { useIntl } from 'react-intl'
import productLabels from 'i18n/productLabels'
import tourGlossaryMessages from 'hooks/Tours/tourGlossaryMessages'

function useTourStyledLabels() {
  const intl = useIntl()

  return useMemo(() => {
    const styledProductTypeTitles: Record<App.Tours.V2ProductTypeStyled, string> = {
      deluxe_tour: intl.formatMessage({
        id: 'C23n9vc69HQVczPw0q7C',
        defaultMessage: 'Deluxe',
      }),
      premium_tour: intl.formatMessage({
        id: 'VWQ3t7-7GdR20QlFbd_E',
        defaultMessage: 'Premium',
      }),
      ultra_lux_tour: intl.formatMessage(productLabels.ultraLux),
      signature_series_tour: intl.formatMessage({
        id: 'SGzBydaebDZ8CnxQ6gFM',
        defaultMessage: 'Signature Series',
      }),
      custom_tour: intl.formatMessage({
        id: 'CR2Mqw1P4INsuaA5rRfZ',
        defaultMessage: 'Custom',
      }),
      insider_series_tour: intl.formatMessage({
        id: 'ibfwnTAGxkDp3ybhfvtD',
        defaultMessage: 'Insider Series',
      }),
    }

    const styledHeading: Record<App.Tours.V2ProductTypeStyled, string> = {
      deluxe_tour: intl.formatMessage(tourGlossaryMessages.keyBenefitsOf, {
        tourVariantName: intl.formatMessage(tourGlossaryMessages.deluxeTours),
      }),
      premium_tour: intl.formatMessage(tourGlossaryMessages.keyBenefitsOf, {
        tourVariantName: intl.formatMessage(tourGlossaryMessages.premiumTours),
      }),
      ultra_lux_tour: intl.formatMessage(tourGlossaryMessages.keyBenefitsOf, {
        tourVariantName: intl.formatMessage(tourGlossaryMessages.ultraLuxTours),
      }),
      signature_series_tour: intl.formatMessage(tourGlossaryMessages.keyBenefitsOf, {
        tourVariantName: intl.formatMessage(tourGlossaryMessages.signatureSeriesTours),
      }),
      custom_tour: intl.formatMessage({
        id: 'pnBKTW4A-iMcbEj5Q3Ds',
        defaultMessage: 'Key Benefits of your once-in-a-lifetime tour',
      }),
      insider_series_tour: intl.formatMessage(tourGlossaryMessages.keyBenefitsOf, {
        tourVariantName: intl.formatMessage(tourGlossaryMessages.insiderSeriesTours),
      }),
    }

    const privateStyledHeading: Record<App.Tours.V2ProductTypeStyled, string> = {
      deluxe_tour: intl.formatMessage(tourGlossaryMessages.keyBenefitsOf, {
        tourVariantName: intl.formatMessage(tourGlossaryMessages.privateDeluxeTours),
      }),
      premium_tour: intl.formatMessage(tourGlossaryMessages.keyBenefitsOf, {
        tourVariantName: intl.formatMessage(tourGlossaryMessages.privatePremiumTours),
      }),
      ultra_lux_tour: intl.formatMessage(tourGlossaryMessages.keyBenefitsOf, {
        tourVariantName: intl.formatMessage(tourGlossaryMessages.privateUltraLuxTours),
      }),
      signature_series_tour: intl.formatMessage(tourGlossaryMessages.keyBenefitsOf, {
        tourVariantName: intl.formatMessage(tourGlossaryMessages.privateSignatureSeriesTours),
      }),
      custom_tour: intl.formatMessage({
        id: 'L8FPYD8w2w2TxU1k6Mcf',
        defaultMessage: 'Key benefits of your private once-in-a-lifetime tour',
      }),
      insider_series_tour: intl.formatMessage({
        id: 'G7tVoRYxEY7rCcY23zkd',
        defaultMessage: 'Key benefits of your private Insider Series tour',
      }),
    }

    const styledProductTypeHotelBenefits: Record<App.Tours.V2ProductTypeStyled, string> = {
      deluxe_tour: intl.formatMessage({
        id: 'xU91-7Y4YPBh9taho0iM',
        defaultMessage: '4 & 4.5 star hotels',
      }),
      premium_tour: intl.formatMessage({
        id: 'dZ2Aoa5FJNYBfQ5hQ6Pf',
        defaultMessage: '5 star hotels',
      }),
      ultra_lux_tour: intl.formatMessage({
        id: 'IkEBSXPmk7HmpFexzQDu',
        defaultMessage: '5 star hotels & iconic stays',
      }),
      signature_series_tour: intl.formatMessage({
        id: 'BFPuGX81IGOlroqi3_-l',
        defaultMessage: '5 star hotels',
      }),
      custom_tour: intl.formatMessage({
        id: 'cyHDoazpcMtTwNTnY9nz',
        defaultMessage: '4 & 5 star hotels',
      }),
      insider_series_tour: '',
    }

    const packageUpgradeDisplayText = {
      REGULAR: intl.formatMessage({
        id: 'zU159GKzORAYE0CDxuiQ',
        defaultMessage: 'Standard Package',
      }),
      VIP: intl.formatMessage({
        id: 'S8-FScl9Ln2H5LZCAGW9',
        defaultMessage: 'Upgraded Package',
      }),
      PLATINUM: intl.formatMessage({
        id: 'Y_qocUusQWI3GQ2xmEIV',
        defaultMessage: 'Deluxe Package',
      }),
    }

    const tourArtefactTypes: Record<App.Tours.ArtefactType, string> = {
      'invitation-letter': intl.formatMessage({
        id: '2H9OjBgrVd_ugNM0-Mi7',
        defaultMessage: 'Letter of Invitation Form',
      }),
      'visa': intl.formatMessage({
        id: 'gP9hyw14Cys8Vp8a8mO9',
        defaultMessage: 'Copy of Visa into Destination Country',
      }),
      'passport-photo': intl.formatMessage({
        id: 'ir6WDn-pjVoAKO1Z-pKZ',
        defaultMessage: 'Passport',
      }),
      'passport-photograph': intl.formatMessage({
        id: 'VL9vSVAiukQ089GB37Rm',
        defaultMessage: 'Passport-Sized Photo',
      }),
      'booking-form': intl.formatMessage({
        id: 'LZPAS7zai0NzYrnZMFAf',
        defaultMessage: 'Booking Form',
      }),
    }

    return {
      styledProductTypeTitles,
      styledHeading,
      privateStyledHeading,
      styledProductTypeHotelBenefits,
      packageUpgradeDisplayText,
      tourArtefactTypes,
    }
  }, [intl])
}

export default useTourStyledLabels
